import { useState } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import { DataTable } from '../../../components/ui/data-table';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Plus, Filter, Package2, AlertTriangle, Clock } from 'lucide-react';
import { columns } from './columns';
import { useInventory } from '../hooks/useInventory';
import { InventoryFilters } from './InventoryFilters';
import { NewItemDialog } from './NewItemDialog';
import { EditItemDialog } from './EditItemDialog';
import { ViewHistoryDialog } from './ViewHistoryDialog';
import { InventoryItem } from '../types/inventory';
import { Card, CardContent } from '../../../components/ui/card';

export function InventoryList() {
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showNewItem, setShowNewItem] = useState(false);
  const [showEditItem, setShowEditItem] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  
  const debouncedSearch = useDebounce(search, 300);

  const { data: items, isLoading } = useInventory({
    search: debouncedSearch,
  });

  const lowStockItems = items?.filter((item: InventoryItem) => item.quantity <= item.minThreshold) || [];
  const expiringItems = items?.filter((item: InventoryItem) => {
    if (!item.expirationDate || !item.minExpirationThreshold) return false;
    const expiryDate = new Date(item.expirationDate);
    const today = new Date();
    const daysUntilExpiration = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= item.minExpirationThreshold;
  }) || [];

  const handleEdit = (item: InventoryItem) => {
    setSelectedItem(item);
    setShowEditItem(true);
  };

  const handleViewHistory = (item: InventoryItem) => {
    setSelectedItem(item);
    setShowHistory(true);
  };

  const handleSaveEdit = (updatedItem: Partial<InventoryItem>) => {
    // TODO: Implement save edit functionality
    console.log('Saving edited item:', updatedItem);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-foreground">Inventory Management</h1>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowFilters(true)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button 
              size="sm"
              onClick={() => setShowNewItem(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Package2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <h3 className="text-2xl font-semibold">{items?.length || 0}</h3>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-destructive/10 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Low Stock Items</p>
                  <h3 className="text-2xl font-semibold">{lowStockItems.length}</h3>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-warning/10 rounded-lg">
                  <Clock className="h-6 w-6 text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expiring Soon</p>
                  <h3 className="text-2xl font-semibold">{expiringItems.length}</h3>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center gap-4">
              <Input
                placeholder="Search inventory..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
              />
            </div>

            <DataTable
              columns={columns({
                onEdit: handleEdit,
                onViewHistory: handleViewHistory,
              })}
              data={items || []}
              isLoading={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      <InventoryFilters
        open={showFilters}
        onOpenChange={setShowFilters}
      />

      <NewItemDialog
        open={showNewItem}
        onOpenChange={setShowNewItem}
      />

      <EditItemDialog
        open={showEditItem}
        onOpenChange={setShowEditItem}
        item={selectedItem}
        onSave={handleSaveEdit}
      />

      <ViewHistoryDialog
        open={showHistory}
        onOpenChange={setShowHistory}
        item={selectedItem}
      />
    </div>
  );
}
